import { useModel, useRoute } from "@bcm-dev/bcms-vite";
import { useMultiPay } from "compid/workflows/MultiPay";
import { Autopay, Bill, Payment } from "model/";

import ButtonContainer from "compid/shared/containers/ButtonContainer";
import BillerView from "compid/shared/views/BillerView";
import UIButton from "compid/ui/UIButton";
import UICheckbox from 'compid/ui/UICheckbox';
import { SVGAsset } from "res";
import BottomPaymentInfo from "../BottomPaymentInfo";
import BillerBadgeView from "../BillerBadgeView";

export default function PaidUpcomingBillerView(props) {
    const { payments } = useModel();
    const navigate = useRoute();

    const { toggleAdd, state, submit } = useMultiPay();
    
    const addedBillerViewIDs = [];
    
    const hasDueBills = id => Bill.initAll().filter(bill => {
        if (bill.biller.id === id && bill.status.toLowerCase() === "due") {
            return bill.reminder
                ? bill.reminder.daysToRemindMeInAdvanced > bill.reminder.dueDate.diff()
                : true;
        }

        return false;
    }, []);

    const billerInGroup = biller => {
        if (props.group === "default") {
            return biller.billerGroup === undefined;
        }

        return biller.billerGroup !== undefined
            ? props.group !== undefined ? biller.billerGroup.id === props.group : true
            : props.group === undefined;
    };

    const { scheduled, processed, processing, canceled } = Bill.initAll()
        .reduce((result, bill) => {
            const lastPayment = Payment.getLastPaymentByBillerID(bill.biller.id, payments);
            const billerIsAdded = bill.biller.addType !== "quick_add" && bill.biller.addType !== "verification_add";
            const billerValidStatus = bill.status.toLowerCase() !== "due";
            // A biller can have multiple bills.
            // If any of those bills are "due", do not show this biller here.
            const billerHasDueBills = hasDueBills(bill.biller.id).length > 0;
            const didAddBillerView = addedBillerViewIDs.indexOf(bill.biller.id) > -1;
            // Keep track of added "Billers" so that we don't show them twice.
            if(!didAddBillerView) {
                addedBillerViewIDs.push(bill.biller.id);
            }

            const showBillerView = billerValidStatus && lastPayment && billerIsAdded && !billerHasDueBills && !didAddBillerView;

            if (showBillerView && billerInGroup(bill.biller)) {
                result.push(bill);
            }

            return result;
        }, [])

        .sort((a, b) => {
            const aLastPayment = Payment.getLastPaymentByBillerID(a.biller.id, payments);
            const bLastPayment = Payment.getLastPaymentByBillerID(b.biller.id, payments);

            if (aLastPayment.date.isBefore(bLastPayment.date)) { return 1; }
            else if (aLastPayment.date.isAfter(bLastPayment.date)) { return -1; }
            else { return 0; }
        })

        .reduce((result, bill) => {
            const lastPayment = Payment.getLastPaymentByBillerID(bill.biller.id, payments);
            const isIncluded = state.includes(bill.id);
            const shouldDisable = state.length > 0;
            const disabledClass = shouldDisable ? "disabled-gray" : "";

            const onClick = evt => !shouldDisable
                ? navigate(`/makeAPayment/${ bill.id }`)
                : undefined;

            const hasAutopays = Autopay.initAll().filter(item => item.billerID === bill.biller.id).length > 0;
            const showPayIcon = bill.biller.paymentType === "instantPayment" || bill.biller.paymentType === "withinMinutes";
            const hasEbills = bill.biller.eBillStatus ? bill.biller.eBillStatus.toLowerCase() === "active" : false;
            const hasLastPayment = lastPayment.status.toLowerCase() !== "none";

            result[lastPayment.status].push(
                <BillerView
                    key={ "biller_" + bill.biller.id + "_bill_" + bill.id }
                    biller={ bill.biller }
                    className={ isIncluded ? "selected-biller-view paid-upcoming-biller-view" : "paid-upcoming-biller-view" }
                    checkbox={ <UICheckbox value={ isIncluded } onChange={ value => toggleAdd(value, bill.id) }
                                dataPoint={`My_Billers_Biller_Checkbox_${ bill.biller.id }`}/> }
                    showPaymentInfo={false}
                    showPaymentInfoBottom={ !(lastPayment.status.toLowerCase() === "canceled") }
                    dataPoint={ `My_Billers_Biller` }
                    billerBadge={hasAutopays || hasEbills ? <BillerBadgeView hasAutopay={hasAutopays} hasEbills={hasEbills} /> : null}
                    bottomPaymentInfo={
                       hasLastPayment &&
                        <BottomPaymentInfo 
                            description={lastPayment.status.toLowerCase() === "scheduled" ? "Next payment": "Last payment"}
                            amount={ lastPayment.amount }
                            showAutopay={ hasAutopays }
                            status={ lastPayment.status }
                            date={ lastPayment.formatDate }
                            dataPoint={ `My_Billers_Payment`}
                            payment={ lastPayment } />
                    }
                    buttons={
                        <>
                            { !isIncluded &&
                                <ButtonContainer className="set-width">
                                    <UIButton className={ `secondary-button ${ disabledClass }` } onClick={ onClick }
                                    dataPoint={ `My_Billers_Button_Biller_${ bill.biller.id }` }>Pay{ showPayIcon && <SVGAsset id="ZapIcon" className="zap-icon" /> }</UIButton>
                                </ButtonContainer> }

                            { isIncluded &&
                                <ButtonContainer className="set-width">
                                    <UIButton 
                                        className="primary-button multipay-btn" 
                                        dataPoint={ `My_Billers_Button_Biller_${ bill.biller.id }` }
                                        onClick={ submit }>Pay { state.length } biller{ state.length > 1 ? "s" : "" }</UIButton>
                                </ButtonContainer> }
                        </>
                    }
                    upsell={ props.getUpSale(bill) } />
            );

            return result;
        }, { "scheduled" : [], "processed" : [], "processing" : [], "canceled": [] } );

    const bills = [ ...scheduled, ...processing, ...processed, ...canceled ];
    return <> { bills } </>;
}